/* eslint-disable max-lines-per-function */
import { authenticatedFetch } from "@lumi/data";
import { SelectValue } from "@radix-ui/react-select";
import { useState, type ReactElement } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";
import { Button } from "../../../shared/components/ui/button";
import { Input } from "../../../shared/components/ui/input";
import { Label } from "../../../shared/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../../../shared/components/ui/select";
import { Switch } from "../../../shared/components/ui/switch";
import { useToast } from "../../../shared/hooks/use-toast";
import type { CollectionCardType, EditableCollectionTemplate } from "../types/collections";

interface CreateCollectionConfigEditorProps {
  readonly onSuccess: (createdKey: string) => void;
  readonly onCancel: () => void;
}

/**
 * CreateCollectionConfigEditor - Inline editor for creating new collections
 *
 * Displays a form for creating a new collection that appears inline in the
 * CollectionDetailsView when a phantom collection is selected. Matches the
 * pattern of CollectionConfigEditor for consistent UX.
 */
export function CreateCollectionConfigEditor({ onSuccess, onCancel }: CreateCollectionConfigEditorProps): ReactElement {
  const { toast } = useToast();
  const { accessToken, refreshAuth } = useAuth();
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [sort, setSort] = useState("0");
  const [template, setTemplate] = useState<EditableCollectionTemplate>("RAIL");
  const [cardType, setCardType] = useState<CollectionCardType>("VERTICAL");
  const [visible, setVisible] = useState(true);
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

  // Real-time key validation
  const validateKey = (value: string): string | null => {
    if (!value) return null;
    if (!/^[a-z0-9-]+$/.test(value)) {
      return "Key must contain only lowercase letters, numbers, and hyphens";
    }
    if (value.startsWith("-") || value.endsWith("-")) {
      return "Key cannot start or end with a hyphen";
    }
    return null;
  };

  /** Determine the cardType to send based on template selection */
  const getCardTypeForTemplate = (): "VERTICAL" | "HORIZONTAL" | "SQUARE" => {
    if (template === "RAIL") return cardType;
    if (template === "PROVIDER") return "SQUARE";
    return "VERTICAL";
  };

  const keyError = validateKey(key);

  const handleCreate = async () => {
    if (!accessToken) return;

    if (!key.trim()) {
      setMessage({ type: "error", text: "Key is required" });
      return;
    }

    if (!name.trim()) {
      setMessage({ type: "error", text: "Name is required" });
      return;
    }

    const sortNum = Number.parseInt(sort, 10);
    if (Number.isNaN(sortNum)) {
      setMessage({ type: "error", text: "Sort must be a valid number" });
      return;
    }

    setCreating(true);
    setMessage(null);

    try {
      await authenticatedFetch(
        `${API_BASE}/v1/admin/collections`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            key: key.trim(),
            name: name.trim(),
            sort: sortNum,
            template,
            cardType: getCardTypeForTemplate(),
            visible,
          }),
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      setMessage({ type: "success", text: "Collection created successfully" });
      // Brief delay to show success message before transitioning
      setTimeout(() => {
        toast({
          description: `Collection "${name.trim()}" created successfully`,
        });
        onSuccess(key.trim());
      }, 300);
    } catch (err) {
      console.error("❌ Failed to create collection:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to create collection";
      toast({
        description: errorMessage,
        variant: "destructive",
      });
      setMessage({
        type: "error",
        text: errorMessage,
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-white mb-2">Create New Collection</h3>
        <p className="text-xs text-neutral-400">Add a new collection to organize your content</p>
      </div>

      {message && (
        <div
          className={`p-3 border rounded ${
            message.type === "success" ? "bg-green-900/20 border-green-800" : "bg-red-900/20 border-red-800"
          }`}
        >
          <p className={`text-sm ${message.type === "success" ? "text-green-400" : "text-red-400"}`}>{message.text}</p>
        </div>
      )}

      <div className="space-y-4 bg-none p-6">
        <div className="space-y-2">
          <Label htmlFor="new-collection-key">
            Key <span className="text-red-400">*</span>
          </Label>
          <Input
            id="new-collection-key"
            type="text"
            value={key}
            onChange={(e) => setKey(e.target.value.toLowerCase())}
            placeholder="e.g., trending"
            className={`bg-transparent ${keyError ? "border-red-500" : ""}`}
          />
          {keyError ? (
            <p className="text-xs text-red-400">{keyError}</p>
          ) : (
            <p className="text-xs text-neutral-500">Unique identifier (lowercase, numbers, hyphens)</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-collection-name">
            Name <span className="text-red-400">*</span>
          </Label>
          <Input
            id="new-collection-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Trending Now"
            className="bg-transparent"
          />
          <p className="text-xs text-neutral-500">Display name shown to users</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-collection-sort">Sort Order</Label>
          <Input
            id="new-collection-sort"
            type="number"
            className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none bg-transparent"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            placeholder="0"
          />
          <p className="text-xs text-neutral-500">
            Lower values appear first (0-9: system, 10-19: distributors, 20+: custom)
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-collection-template">Template</Label>
          <Select value={template} onValueChange={(value) => setTemplate(value as "RAIL" | "MAP_REVEAL" | "PROVIDER")}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select template" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="RAIL">Content Rail</SelectItem>
              <SelectItem value="MAP_REVEAL">Map Reveal Section</SelectItem>
              <SelectItem value="PROVIDER">Provider Showcase</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-neutral-500">Choose the layout template for this collection</p>
        </div>

        {template === "RAIL" && (
          <div className="space-y-2">
            <Label htmlFor="new-collection-card-type">Card Type</Label>
            <Select
              value={cardType}
              onValueChange={(value) => setCardType(value as "VERTICAL" | "HORIZONTAL" | "SQUARE")}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select card type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="VERTICAL">Vertical (2:3 Portrait)</SelectItem>
                <SelectItem value="HORIZONTAL">Horizontal (16:9 Landscape)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-neutral-500">Choose how titles will be displayed in this collection</p>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="new-collection-visible">Visibility</Label>
          <div className="flex items-center gap-3">
            <Switch id="new-collection-visible" checked={visible} onCheckedChange={setVisible} />
            <span className="text-sm text-neutral-400">
              {visible ? "Visible on home page" : "Hidden from home page"}
            </span>
          </div>
          <p className="text-xs text-neutral-500">Hidden collections won't appear on the home page</p>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button onClick={onCancel} variant="outline" size="sm" disabled={creating}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleCreate()}
            disabled={creating || !key.trim() || !name.trim() || !!keyError}
            variant="outline"
            size="sm"
            className="hover:bg-green-500/10 hover:text-green-500 hover:border-green-500/50"
          >
            {creating ? "Creating..." : "Create Collection"}
          </Button>
        </div>
      </div>
    </div>
  );
}
